/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Modal, Tabs } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { paragraphMedium, weightRegular } from 'src/components/css'
import Text from 'src/components/ts-components/Text'
import { useCallback, useState, useEffect, useMemo } from 'react'
import { useModalScroll } from 'src/hooks/useModalScroll'
import useWindowSize from 'src/hooks/useWindowSize'
import {
  RegistrationDetailsModalProvider,
  useRegistrationDetailsModal,
} from './context'
import RegistrationDetailsForm from './forms/RegistrationDetailsForm'
import Button from 'src/components/ts-components/Button'
import RegistrationBillingForm from './forms/RegistrationBillingForm'
import { Country } from '@sportninja/common/hooks/useCountries'
import { RegistrationTypes } from '@sportninja/common/types/Registration'

const styles = css`
  .my-modal-body {
  }
  .my-modal-mask {
    backdrop-filter: blur(1px);
  }
  .my-modal-header {
    border-bottom: 1px dotted ${colors.FIERY_RED};
  }
  .my-modal-footer {
    color: ${colors.FIERY_RED};
  }
  .my-modal-content {
    border: 1px solid #333;
  }
`
const classNames = {
  body: styles['my-modal-body'],
  mask: styles['my-modal-mask'],
  header: styles['my-modal-header'],
  footer: styles['my-modal-footer'],
  content: styles['my-modal-content'],
}
const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
    maxHeight: 'calc(100vh - 50px)!important',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    // backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

const labelById = {
  0: 'Details',
  1: 'Billing',
}

interface RegistrationDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  scheduleId: string
  registrationId?: string | null
  countries: Country[]
  types: RegistrationTypes | null
}

const RegistrationDetailsModalContent = ({
  isOpen,
  onClose,
}: RegistrationDetailsModalProps) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const [activeTab, setActiveTab] = useState('0')
  const { openModal, closeModal } = useModalScroll()
  const { registrationDetails } = useRegistrationDetailsModal()
  const isTeamRegistration = useMemo(
    () => registrationDetails?.registration_type === 'Team',
    [registrationDetails]
  )

  const hasBilling = useMemo(() => {
    return (
      registrationDetails?.billing && registrationDetails.billing.length > 0
    )
  }, [registrationDetails?.billing])

  useEffect(() => {
    if (isOpen) {
      openModal()
    } else {
      closeModal()
    }
  }, [closeModal, isOpen, openModal])

  const getHeader = useCallback(() => {
    if (isTeamRegistration) {
      return (
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          {`Registration${
            registrationDetails?.team?.name
              ? ` - ${registrationDetails.team.name}`
              : ''
          }`}
        </Text>
      )
    }

    return (
      <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
        {`Registration${
          registrationDetails?.player?.name_first
            ? ` - ${registrationDetails.player.name_first} ${registrationDetails.player.name_last}`
            : ''
        }`}
      </Text>
    )
  }, [
    isTeamRegistration,
    registrationDetails?.player?.name_first,
    registrationDetails?.player?.name_last,
    registrationDetails?.team?.name,
  ])

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 16px;
          cursor: pointer;
        `}
      />
    )
  }, [])

  const getFormById = (id: string) => {
    switch (id) {
      case '0':
        return <RegistrationDetailsForm />
      case '1':
        return <RegistrationBillingForm />
      default:
        return null
    }
  }

  const getFooter = useCallback(() => {
    return (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          width: ${isMobile ? '100%' : '33%'};
          margin-left: ${isMobile ? '0' : 'auto'};
          @media (max-width: 768px) {
            width: 100%;
            flex-direction: column;
          }
        `}
      >
        <Button
          text='Cancel'
          onClick={() => {
            onClose()
            setActiveTab('0')
          }}
          variant='secondary'
        />
        <Button
          text='Save'
          onClick={() => {
            // Add save logic here
            onClose()
            setActiveTab('0')
          }}
          variant='primary'
        />
      </div>
    )
  }, [isMobile, onClose])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        background: ${colors.SECONDARY_400} !important;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '80vw'};
        border-radius: ${isMobile ? '0' : '8px'} !important;
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'calc(100vh - 50px)'} !important;
          border-radius: ${isMobile ? '0' : '8px'} !important;
          display: flex;
          flex-direction: column;
        }
        .ant-modal-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .ant-modal-title {
          padding: 8px !important;
        }
      `}
      destroyOnClose={true}
      width={isMobile ? '100%' : '80vw'}
      closeIcon={closeIcon()}
      title={getHeader()}
      open={isOpen}
      onCancel={onClose}
      centered={!isMobile}
      footer={getFooter()}
      classNames={classNames}
      styles={modalStyles}
    >
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        css={css`
          display: flex;
          flex-direction: column;
          height: 100%;
          .ant-tabs-nav {
            margin: 0;
            position: sticky;
            top: 0;
            z-index: 1;
            background: ${colors.SECONDARY_300};
          }
          .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
            color: ${colors.WHITE} !important;
          }
          .ant-tabs-nav-wrap {
            height: 48px;
          }
          .ant-tabs-tab {
            padding: 8px 16px;
            margin: 0 !important;
          }
          .ant-tabs-tab-btn {
            ${paragraphMedium}
            ${weightRegular}
          }
          .ant-tabs-tab-active {
            background-color: #26303e;
          }
          .ant-tabs-content-holder {
            flex: 1;
            overflow-y: auto;
          }
          .ant-tabs-content {
            height: 100%;
          }
        `}
        items={Object.entries(labelById)
          .filter(([key]) => key === '0' || (key === '1' && hasBilling))
          .map(([key, label]) => ({
            label,
            key,
            children: getFormById(key),
          }))}
      />
    </Modal>
  )
}

const RegistrationDetailsModal = (props: RegistrationDetailsModalProps) => {
  return (
    <RegistrationDetailsModalProvider
      scheduleId={props.scheduleId}
      registrationId={props.registrationId || undefined}
      countries={props.countries}
      types={props.types}
    >
      <RegistrationDetailsModalContent {...props} />
    </RegistrationDetailsModalProvider>
  )
}

export default RegistrationDetailsModal
